import * as Sentry from '@sentry/react';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@/env';
import { BackendType } from './types';

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  failedQueue = [];
};

const getBaseURL = (backendType?: BackendType): string | undefined => {
  if (backendType === BackendType.CPMS) {
    if (!env.VITE_CPMS_BACKEND_URL) {
      throw new Error('VITE_CPMS_BACKEND_URL is not configured');
    }
    return env.VITE_CPMS_BACKEND_URL;
  }
  return env.VITE_BACKEND_URL;
};

// Default per-request timeout so a stalled backend rejects (ECONNABORTED) instead of
// hanging indefinitely and leaving loads stuck. Individual calls can override via config.
const REQUEST_TIMEOUT_MS = 30_000;

const baseApi = axios.create({
  timeout: REQUEST_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    ...(env.VITE_ZUMO_TOKEN ? { 'X-Zumo-Auth': env.VITE_ZUMO_TOKEN } : {}),
  },
});

baseApi.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const backendType = config.backendType ?? BackendType.DEFAULT;
    const baseURL = getBaseURL(backendType);
    // Leave baseURL unset when undefined so axios falls back to same-origin requests.
    if (baseURL !== undefined) config.baseURL = baseURL;

    const session = await fetchAuthSession();
    const accessToken = session.tokens?.accessToken?.toString();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

baseApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    const status = error.response?.status;
    // Only 401 signals a stale token; other 4xx are caller errors and must not
    // trigger a Cognito refresh + replay.
    const shouldRefresh = status === 401;

    if (shouldRefresh && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (token && originalRequest) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return baseApi(originalRequest);
            }
            return Promise.reject(error);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newSession = await fetchAuthSession({ forceRefresh: true });
        const newAccessToken = newSession.tokens?.accessToken?.toString();

        isRefreshing = false;

        if (newAccessToken) {
          processQueue(null, newAccessToken);
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return baseApi(originalRequest);
        }

        processQueue(error, null);
        return Promise.reject(error);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      }
    }

    // Expected 4xx (validation, not-found, forbidden) are handled by callers;
    // only network failures and server errors are Sentry-worthy.
    if (status === undefined || status >= 500) {
      const backendType = originalRequest?.backendType ?? BackendType.DEFAULT;
      Sentry.captureException(error, {
        tags: { backendType },
        extra: {
          baseURL: originalRequest?.baseURL,
          endpoint: originalRequest?.url,
          method: originalRequest?.method,
        },
      });
    }

    return Promise.reject(error);
  },
);

export default baseApi;

/** Typed wrapper that routes requests at the CPMS backend (see BackendType). */
export const cpmsApi = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    baseApi.get<T>(url, { ...config, backendType: BackendType.CPMS }),
  post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    baseApi.post<T>(url, data, { ...config, backendType: BackendType.CPMS }),
  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    baseApi.put<T>(url, data, { ...config, backendType: BackendType.CPMS }),
  patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    baseApi.patch<T>(url, data, { ...config, backendType: BackendType.CPMS }),
  delete: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    baseApi.delete<T>(url, { ...config, backendType: BackendType.CPMS }),
};
